const e = up.element;
const u = up.util;
up.Layer = class Layer extends up.Record {
    keys() {
        return [
            'element',
            'stack',
            'history',
            'mode',
            'context',
            'lastScrollTops',
            'lastFocusCapsules',
        ];
    }
    defaults() {
        return {
            context: {},
            lastScrollTops: up.viewport.newStateCache(),
            lastFocusCapsules: up.viewport.newStateCache()
        };
    }
    constructor(options = {}) {
        super(options);
        u.assert(this.mode);
    }
    setupHandlers() {
        up.link.convertClicks(this);
        this._unbindLocationChanged = up.on('up:location:changed', (event) => this._onBrowserLocationChanged(event));
    }
    teardownHandlers() {
        var _a;
        (_a = this._unbindLocationChanged) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    mainTargets() {
        return up.layer.mainTargets(this.mode);
    }
    sync() {
    }
    accept() {
        throw new up.NotImplemented();
    }
    dismiss() {
        throw new up.NotImplemented();
    }
    peel(options) {
        this.stack.peel(this, options);
    }
    evalOption(option) {
        return u.evalOption(option, this);
    }
    isCurrent() {
        return this.stack.isCurrent(this);
    }
    isFront() {
        return this.stack.isFront(this);
    }
    isRoot() {
        return this.stack.isRoot(this);
    }
    isOverlay() {
        return this.stack.isOverlay(this);
    }
    isAlive() {
        throw new up.NotImplemented();
    }
    assertAlive() {
        if (!this.isAlive()) {
            throw new up.Aborted("Layer is no longer alive");
        }
    }
    get parent() {
        return this.stack.parentOf(this);
    }
    get child() {
        return this.stack.childOf(this);
    }
    get ancestors() {
        return this.stack.ancestorsOf(this);
    }
    get descendants() {
        return this.stack.descendantsOf(this);
    }
    get subtree() {
        return [this, ...this.descendants];
    }
    get index() {
        var _a;
        return (_a = this._index) !== null && _a !== void 0 ? _a : (this._index = this.stack.indexOf(this));
    }
    getContentElement() {
        return this.contentElement || this.element;
    }
    getBoxElement() {
        return this.boxElement || this.element;
    }
    getFocusElement() {
        return this.getBoxElement();
    }
    getFirstSwappableElement() {
        throw new up.NotImplemented();
    }
    contains(element) {
        return element.closest(up.layer.anySelector()) === this.element;
    }
    on(...args) {
        return this._buildEventListenerGroup(args).bind();
    }
    off(...args) {
        return this._buildEventListenerGroup(args).unbind();
    }
    _buildEventListenerGroup(args) {
        return up.EventListenerGroup.fromBindArgs(args, {
            guard: (event) => this._containsEventTarget(event),
            elements: [this.element],
            baseLayer: this
        });
    }
    _containsEventTarget(event) {
        return this.contains(event.target);
    }
    wasHitByMouseEvent({ clientX, clientY }) {
        const hittableElement = document.elementFromPoint(clientX, clientY);
        return !hittableElement || this.contains(hittableElement);
    }
    _buildEventEmitter(args) {
        return up.EventEmitter.fromEmitArgs(args, { layer: this });
    }
    emit(...args) {
        return this._buildEventEmitter(args).emit();
    }
    saveHistory() {
        u.assert(this.isFront());
        if (!this.showsLiveHistory())
            return;
        this._savedTitle = this.title;
        this._savedMetaTags = this.metaTags;
        this._savedLocation = this.location;
        this._savedLang = this.lang;
    }
    restoreHistory() {
        if (!this.showsLiveHistory())
            return;
        if (this._savedLocation) {
            up.history.push(this._savedLocation);
        }
        if (this._savedTitle) {
            document.title = this._savedTitle;
        }
        if (this._savedMetaTags) {
            up.history.updateMetaTags(this._savedMetaTags);
        }
        if (u.isString(this._savedLang)) {
            up.history.updateLang(this._savedLang);
        }
    }
    asCurrent(fn) {
        return this.stack.asCurrent(this, fn);
    }
    updateHistory(options) {
        var _a, _b;
        if (u.isString(options.location)) {
            this._updateLocation(options.location, { push: true });
        }
        if (up.history.config.updateMetaTags && u.isList(options.metaTags)) {
            (_b = (_a = up.migrate) === null || _a === void 0 ? void 0 : _a.warnOfHungryMetaTags) === null || _b === void 0 ? void 0 : _b.call(_a, options.metaTags);
            this._updateMetaTags(options.metaTags);
        }
        if (u.isString(options.title)) {
            this._updateTitle(options.title);
        }
        if (u.isString(options.lang)) {
            this._updateLang(options.lang);
        }
    }
    showsLiveHistory() {
        return this.history && this.isFront();
    }
    _onBrowserLocationChanged({ location }) {
        if (this.showsLiveHistory()) {
            this._updateLocation(location, { push: false });
        }
    }
    get title() {
        if (this.showsLiveHistory()) {
            return document.title;
        }
        else {
            return this._savedTitle;
        }
    }
    _updateTitle(title) {
        this._savedTitle = title;
        if (this.showsLiveHistory()) {
            document.title = title;
        }
    }
    get metaTags() {
        if (this.showsLiveHistory()) {
            return up.history.findMetaTags();
        }
        else {
            return this._savedMetaTags;
        }
    }
    _updateMetaTags(metaTags) {
        this._savedMetaTags = metaTags;
        if (this.showsLiveHistory()) {
            up.history.updateMetaTags(metaTags);
        }
    }
    get lang() {
        if (this.showsLiveHistory()) {
            return up.history.getLang();
        }
        else {
            return this._savedLang;
        }
    }
    _updateLang(lang) {
        this._savedLang = lang;
        if (this.showsLiveHistory()) {
            up.history.updateLang(lang);
        }
    }
    get location() {
        if (this.showsLiveHistory()) {
            return up.history.location;
        }
        else {
            return this._savedLocation;
        }
    }
    _updateLocation(newLocation, { push }) {
        let prevSavedLocation = this._savedLocation;
        let liveLocation = up.history.location;
        this._savedLocation = newLocation;
        if (newLocation !== liveLocation && this.showsLiveHistory() && push) {
            up.history.push(newLocation);
        }
        if (newLocation !== prevSavedLocation && this.state !== 'opening') {
            this.emit('up:layer:location:changed', { location: newLocation, previousLocation: prevSavedLocation, log: false });
        }
    }
    selector(part) {
        return this.constructor.selector(part);
    }
    static selector(_part) {
        throw new up.NotImplemented();
    }
    toString() {
        throw new up.NotImplemented();
    }
    affix(...args) {
        return e.affix(this.getFirstSwappableElement(), ...args);
    }
    [u.isEqual.key](other) {
        return (this.constructor === other.constructor) && (this.element === other.element);
    }
    hasFocus() {
        let focusedElement = document.activeElement;
        return focusedElement !== document.body && this.element.contains(focusedElement);
    }
    reset() {
        Object.assign(this, this.defaults());
    }
};
